import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getToDoList } from "store/actions/toDoActions";
import { hasValidTokens } from 'utils/authUtils';
import { SearchBox } from "@fluentui/react";
import { TooltipHost } from '@fluentui/react';

const PortalTaskView = ({ id, callbackUrl }) => {
  const dispatch = useDispatch();
  const { loading, data } = useSelector((state) => state.todo);

  const [sortField, setSortField] = useState("startDate");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pageIndex, setPageIndex] = useState(0);
  const [allItems, setAllItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const scrollContainerRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const pageSize = 50;
  const [searchValue, setSearchValue] = useState('');
  const [searchText, setSearchText] = useState('');

  const handleRefresh = () => {
    setSearchText('');
    setSearchValue('');
    setPageIndex(0);
    setAllItems([]);
    setHasMore(true);
    dispatch(
      getToDoList({
        portalDataSourceId: id,
        sort: sortField,
        order: sortOrder,
        pageIndex: 0,
        pageSize: pageSize,
        status: "pending",
      })
    );
  };

  // Initial load and sort change
  useEffect(() => {
    if (!id) return;

    setPageIndex(0);
    setAllItems([]);
    setHasMore(true);

    dispatch(
      getToDoList({
        portalDataSourceId: id,
        sort: sortField,
        order: sortOrder,
        pageIndex: 0,
        pageSize: pageSize,
        status: "pending",
        filterKey: searchText,
      })
    );
  }, [dispatch, id, sortField, sortOrder, searchText]);

  // Accumulate items when new data arrives
  useEffect(() => {
    if (data && data.length > 0) {
      if (pageIndex === 0 || isRefreshingRef.current) {
        setAllItems(data);
        isRefreshingRef.current = false;
      } else {
        setAllItems((prev) => [...prev, ...data]);
      }
      setHasMore(data.length === pageSize);
    } else if (data && data.length === 0 && pageIndex === 0) {
      setAllItems([]);
      setHasMore(false);
    }
  }, [data, pageIndex, pageSize]);

  // Auto-refresh every 20 seconds
  useEffect(() => {
    if (!id) return;

    if (!hasValidTokens()) {
      return;
    }

    const interval = setInterval(() => {
      const currentLength = allItems.length || pageSize;
      isRefreshingRef.current = true;
      dispatch(
        getToDoList({
          portalDataSourceId: id,
          sort: sortField,
          order: sortOrder,
          pageIndex: 0,
          pageSize: currentLength,
          status: "pending",
          filterKey: searchText,
          silent: true,
        })
      );
    }, 20000);

    return () => clearInterval(interval);
  }, [dispatch, id, sortField, sortOrder, allItems.length, pageSize, searchText]);

  // Load more items
  const loadMoreItems = useCallback(() => {
    if (!loading && hasMore && id) {
      const nextPage = pageIndex + 1;
      setPageIndex(nextPage);
      dispatch(
        getToDoList({
          portalDataSourceId: id,
          sort: sortField,
          order: sortOrder,
          pageIndex: nextPage,
          pageSize,
          status: "pending",
          filterKey: searchText,
        })
      );
    }
  }, [loading, hasMore, pageIndex, dispatch, id, sortField, sortOrder, pageSize, searchText]);

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;

      // Load more when scrolled to 80% of content
      if (scrollHeight - scrollTop <= clientHeight * 1.2) {
        loadMoreItems();
      }
    },
    [loadMoreItems]
  );

  const STATUS_CONFIG = {
    2: { label: "In Progress", className: "in-progress" },
    1: { label: "Not Started", className: "not-started" },
  };

  const handleSort = (field) => {
    let newSortOrder = "asc";
    if (sortField === field) {
      newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    }

    setSortField(field);
    setSortOrder(newSortOrder);
  };

  const getSortIndicator = (field) =>
    sortField === field ? (sortOrder === "asc" ? "↑" : "↓") : "";

  return (
    <>
      <div className="page-action-header" style={{ marginTop: '16px' }}>
        <SearchBox
          style={{ width: '300px' }}
          placeholder="Search here"
          value={searchValue}
          onChange={(_, newValue) => {
            const limited = (newValue ?? '').slice(0, 40);
            setSearchValue(limited);
            if ((newValue ?? '').trim() === '') {
              setSearchText('');
              handleRefresh();
            }
          }}
          onSearch={newValue => setSearchText(newValue || '')}
          showIcon={true}
          disableAnimation={true}
          onClear={() => {
            setSearchText('');
            handleRefresh();
          }}
        />
      </div>
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="table-container"
      >
        <table>
        <thead>
          <tr>
            <th
              className="cursor-pointer w-60"
              onClick={() => handleSort("title")}
            >
              Title {getSortIndicator("title")}
            </th>
            <th
              className="cursor-pointer"
              onClick={() => handleSort("assignmentType")}
            >
              Assignment Type {getSortIndicator("assignmentType")}
            </th>
            <th
              className="cursor-pointer"
              onClick={() => handleSort("startDate")}
            >
              Start Date {getSortIndicator("startDate")}
            </th>
            <th
              className="cursor-pointer"
              onClick={() => handleSort("endDate")}
            >
              End Date {getSortIndicator("endDate")}
            </th>
            <th
              className="cursor-pointer"
              onClick={() => handleSort("status")}
            >
              Status {getSortIndicator("status")}
            </th>
          </tr>
        </thead>
        <tbody>
          {loading && pageIndex === 0 ? (
            <tr>
              <td colSpan="100%" className="text-center">
                Loading...
              </td>
            </tr>
          ) : allItems.length === 0 && pageIndex === 0  ? (
            <tr>
              <td colSpan="100%" className="text-center">
                No task found
              </td>
            </tr>
          ) : (
            <>
              {allItems.map((item) => (
                <tr
                  key={`${item.id ?? item.externalId}-${item.assignmentType}`}
                  onClick={() => window.open(item.redirectUrl, "_blank", "noopener,noreferrer")}
                >
                  <td className="w-40">
                    <TooltipHost content={item.formattedMessage}>
                      <div
                        style={{
                          marginLeft: "10px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "25vw"
                        }}
                      >
                        {item.formattedMessage}
                      </div>
                    </TooltipHost>
                  </td>
                  
                  <td>{item.assignmentType || '-'}</td>
                  <td>{item.startDate ? new Date(item.startDate).toLocaleDateString() : "-"}</td>
                  <td>{item.endDate ? new Date(item.endDate).toLocaleDateString() : "-"}</td>
                  <td>
                    <span className={`chip ${STATUS_CONFIG[item.status]?.className || ""}`}>
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
              {loading && pageIndex > 0 && (
                <tr>
                  <td colSpan="100%" className="text-center">
                    Loading more...
                  </td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
      </div>
    </>
  );
};

export default PortalTaskView;
